import { useEffect, useMemo, useState } from 'react'
import { clock, dolumAciklama, dolumEtiketi, dolumOzeti, mwh, price as fmtPrice, qty } from '../format'
import {
  deleteLedgerBots,
  deleteLedgerFills,
  deleteLedgerOrders,
  fetchLedgerBots,
  fetchLedgerContracts,
  fetchLedgerFills,
  fetchLedgerOrders,
  type LedgerBot,
  type LedgerFill,
  type LedgerFilters,
  type LedgerOrder,
} from '../ledger'
import { durumBilgisi } from '../orderStatus'
import { BotReportModal } from './BotReportModal'
import { OrderLogModal } from './OrderLogModal'

/**
 * Kayıt Defteri — kalıcı kaydı inceleme aracı.
 *
 *   Teklifler   · emirler (her durum), filtreli
 *   Eşleşmeler  · dolumlar, dolum anındaki piyasa bağlamıyla
 *
 * Veri doğrudan Postgres'ten geliyor (`/api/ledger/*`), motorun belleğinden
 * değil: buradaki amaç geçmişi incelemek ve geçmiş bellekteki pencereden büyük.
 *
 * Filtre değişince sorgu **yeniden** çalışıyor ve sayfa başa dönüyor — 3.
 * sayfadayken filtreyi daraltıp boş ekran görmek en sinir bozucu hâli olurdu.
 *
 * İstatistik kartları YOK (kullanıcı isteği): sayfa yalnız tablo. Sunucudaki
 * özet hesabı da bu yüzden isteğe bağlı (`summary=true`), her filtre
 * değişiminde boşuna GROUP BY çalışmasın.
 *
 * Satırdaki `ⓘ` emrin tüm hayatını gösteren modalı açar; eşleşme satırında da
 * aynı modal açılır (dolumun ait olduğu emir üzerinden), çünkü "bu eşleşme
 * nereden geldi" sorusunun cevabı emrin geçmişinde.
 */

type TabId = 'orders' | 'fills' | 'bots'
const SAYFA = 50

const DURUMLAR = [
  { value: '', label: 'Tüm durumlar' },
  { value: 'OPEN', label: 'A · Açık' },
  { value: 'FILLED', label: 'M · Tamamen eşleşti' },
  { value: 'CANCELLED', label: 'C · İptal edildi' },
  { value: 'EXPIRED', label: 'E · Süresi doldu' },
]

export function LedgerPage() {
  const [tab, setTab] = useState<TabId>('orders')
  const [contracts, setContracts] = useState<string[]>([])

  // Filtreler iki sekmede ORTAK: kullanıcı kontratı seçip sekme değiştirdiğinde
  // filtreyi baştan kurmak zorunda kalmasın.
  const [contract, setContract] = useState('')
  const [status, setStatus] = useState('')
  const [side, setSide] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [search, setSearch] = useState('')
  //: Mobilde filtre şeridi katlı başlar; masaüstünde bu durum kullanılmıyor.
  const [filtreAcik, setFiltreAcik] = useState(false)
  const [page, setPage] = useState(0)

  const [orders, setOrders] = useState<LedgerOrder[]>([])
  const [fills, setFills] = useState<LedgerFill[]>([])
  const [bots, setBots] = useState<LedgerBot[]>([])
  const [total, setTotal] = useState(0)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [detay, setDetay] = useState<string | null>(null)
  const [botDetay, setBotDetay] = useState<string | null>(null)
  //: Kalıcı silme seçimi — anahtar teklif/botta kimlik, eşleşmede DB serisi.
  const [secim, setSecim] = useState<Set<string | number>>(new Set())
  const [siliniyor, setSiliniyor] = useState(false)
  //: Silme sonrası listeyi yeniden çekmek için sayaç (effect bağımlılığı).
  const [surum, setSurum] = useState(0)

  useEffect(() => {
    fetchLedgerContracts().then(setContracts).catch(() => setContracts([]))
  }, [])

  // Filtre değişince başa dön. `page` bilerek bağımlılık DEĞİL: kendisi
  // sıfırlanınca sonsuz döngü olurdu.
  useEffect(() => setPage(0), [tab, contract, status, side, since, until, search])
  // Görünüm değişince seçim sıfırlanır — görünmeyen satır silinmesin.
  useEffect(() => setSecim(new Set()),
    [tab, contract, status, side, since, until, search, page, surum])

  const filters = useMemo<LedgerFilters>(
    () => ({
      contract: contract || undefined,
      side: side || undefined,
      since: since || undefined,
      until: until || undefined,
      search: search.trim() || undefined,
      limit: SAYFA,
      offset: page * SAYFA,
      ...(tab === 'orders' ? { status: status || undefined } : {}),
    }),
    [tab, contract, status, side, since, until, search, page],
  )

  useEffect(() => {
    let iptal = false
    setYukleniyor(true)
    setHata(null)
    const istek =
      tab === 'orders'
        ? fetchLedgerOrders(filters).then((d) => {
            if (iptal) return
            setOrders(d.rows)
            setTotal(d.total)
          })
        : tab === 'fills'
          ? fetchLedgerFills(filters).then((d) => {
              if (iptal) return
              setFills(d.rows)
              setTotal(d.total)
            })
          : fetchLedgerBots(contract || undefined).then((rows) => {
              if (iptal) return
              setBots(rows)
              setTotal(rows.length)
            })
    istek
      .catch((err) => !iptal && setHata(err instanceof Error ? err.message : 'Kayıtlar alınamadı'))
      .finally(() => !iptal && setYukleniyor(false))
    return () => {
      iptal = true
    }
  }, [tab, filters, contract, surum])

  /** Onaylı kalıcı silme — sekmeye göre uç ve uyarı metni değişir. */
  async function secilenleriSil() {
    if (secim.size === 0 || siliniyor) return
    const n = secim.size
    const mesaj =
      tab === 'orders'
        ? `${n} teklif KALICI olarak silinecek — sürümleri ve eşleşmeleri de gider. ` +
          'Bu işlem geri alınamaz. Emin misin?'
        : tab === 'fills'
          ? `${n} eşleşme KALICI olarak silinecek — net pozisyon ve istatistikler ` +
            'değişir. Bu işlem geri alınamaz. Emin misin?'
          : `${n} bot kaydı TÜM İZİYLE silinecek: kararları, emirleri ve ` +
            'eşleşmeleri dahil. Çalışan/duraklı botlar önce durdurulur ve açık ' +
            'emirleri iptal edilir. Bu işlem geri alınamaz. Emin misin?'
    if (!window.confirm(mesaj)) return
    setSiliniyor(true)
    setHata(null)
    try {
      if (tab === 'orders') await deleteLedgerOrders([...secim] as string[])
      else if (tab === 'fills') await deleteLedgerFills([...secim] as number[])
      else await deleteLedgerBots([...secim] as string[])
      setSurum((v) => v + 1)          // listeyi tazele (seçim de sıfırlanır)
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Silinemedi')
    } finally {
      setSiliniyor(false)
    }
  }

  const sec = (id: string | number, secili: boolean) =>
    setSecim((s) => {
      const yeni = new Set(s)
      if (secili) yeni.add(id)
      else yeni.delete(id)
      return yeni
    })

  const sayfaSayisi = Math.max(1, Math.ceil(total / SAYFA))
  const filtreVar = !!(contract || status || side || since || until || search)

  const temizle = () => {
    setContract('')
    setStatus('')
    setSide('')
    setSince('')
    setUntil('')
    setSearch('')
  }

  // Katlı şeridin rozeti: doldurulmuş filtre sayısı.
  const etkinFiltreSayisi = [contract, status, side, since, until, search].filter(Boolean).length

  return (
    <main className="ledger">
      <div className="ledger-panel">
        <div className="panel-tabs" role="tablist">
          {([
            { id: 'orders', label: 'Teklifler' },
            { id: 'fills', label: 'Eşleşmeler' },
            { id: 'bots', label: 'Bot Hareketleri' },
          ] as { id: TabId; label: string }[]).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`panel-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          <span className="ledger-count">
            {yukleniyor ? 'yükleniyor…' : `${total.toLocaleString('tr-TR')} kayıt`}
          </span>
        </div>

        {/* Filtre şeridi mobilde katlı: altı denetim telefonda ekranın yarısını
            yiyordu, oysa asıl bakılan şey tablo. Düğme YALNIZ mobilde görünür
            (CSS), masaüstünde şerit her zaman açık. Rozet, şerit kapalıyken kaç
            filtrenin etkin olduğunu söylüyor — kapalı bir şeridin arkasında
            unutulmuş filtre en can sıkıcı hâli olurdu. */}
        <button
          type="button"
          className="lf-katla"
          aria-expanded={filtreAcik}
          onClick={() => setFiltreAcik((a) => !a)}
        >
          Filtreler
          {etkinFiltreSayisi > 0 && <span className="lf-rozet">{etkinFiltreSayisi}</span>}
          <span className="lf-ok" aria-hidden="true">
            {filtreAcik ? '▴' : '▾'}
          </span>
        </button>

        <div className={`ledger-filters${filtreAcik ? ' acik' : ''}`}>
          <label className="lf">
            <span>Kontrat</span>
            <select value={contract} onChange={(e) => setContract(e.target.value)}>
              <option value="">Tümü</option>
              {contracts.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          {/* Durum yalnız tekliflerde anlamlı; eşleşmenin durumu olmaz. */}
          {tab === 'orders' && (
            <label className="lf">
              <span>Durum</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {DURUMLAR.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Bot listesinde yön/tarih/arama yok — botun tamamı tek satır zaten. */}
          {tab !== 'bots' && (
            <>
              <label className="lf">
                <span>Yön</span>
                <select value={side} onChange={(e) => setSide(e.target.value)}>
                  <option value="">İkisi</option>
                  <option value="BUY">Alış</option>
                  <option value="SELL">Satış</option>
                </select>
              </label>

              <label className="lf">
                <span>Başlangıç</span>
                <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
              </label>

              <label className="lf">
                <span>Bitiş</span>
                <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
              </label>

              <label className="lf grow">
                <span>Ara</span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={tab === 'orders' ? 'emir kimliği' : 'emir ya da işlem kimliği'}
                />
              </label>
            </>
          )}

          <button
            type="button"
            className="lf-clear"
            onClick={temizle}
            disabled={!filtreVar}
            title="Tüm filtreleri temizle"
          >
            Temizle
          </button>

          {/* Kalıcı silme — seçim varken belirir; onay sorulur, geri alınamaz. */}
          {secim.size > 0 && (
            <button
              type="button"
              className="lf-sil"
              onClick={secilenleriSil}
              disabled={siliniyor}
              title="Seçilen kayıtları veritabanından KALICI olarak siler"
            >
              {siliniyor ? 'Siliniyor…' : `Seçilenleri Sil (${secim.size})`}
            </button>
          )}
        </div>

        {hata && <div className="order-error">{hata}</div>}

        <div className="ledger-body">
          {tab === 'orders' ? (
            <OrdersTable rows={orders} onDetail={setDetay} bos={!yukleniyor}
                         secim={secim} sec={sec} setSecim={setSecim} />
          ) : tab === 'fills' ? (
            <FillsTable rows={fills} onDetail={setDetay} bos={!yukleniyor}
                        secim={secim} sec={sec} setSecim={setSecim} />
          ) : (
            <BotsTable rows={bots} onDetail={setBotDetay} bos={!yukleniyor}
                       secim={secim} sec={sec} setSecim={setSecim} />
          )}
        </div>

        {tab !== 'bots' && (
        <div className="ledger-pager">
          <button
            type="button"
            className="pager-btn"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Önceki sayfa"
          >
            ‹
          </button>
          <span className="pager-label">
            {page + 1}/{sayfaSayisi}
          </span>
          <button
            type="button"
            className="pager-btn"
            onClick={() => setPage((p) => Math.min(sayfaSayisi - 1, p + 1))}
            disabled={page >= sayfaSayisi - 1}
            aria-label="Sonraki sayfa"
          >
            ›
          </button>
        </div>
        )}
      </div>

      {detay && <OrderLogModal orderId={detay} onClose={() => setDetay(null)} />}
      {botDetay && <BotReportModal botId={botDetay} onClose={() => setBotDetay(null)} />}
    </main>
  )
}


/** Bot Hareketleri — her bot TEK satır; ⓘ tam raporu (kararlar, bağlam) açar. */
function BotsTable({
  rows,
  onDetail,
  bos,
  secim,
  sec,
  setSecim,
}: {
  rows: LedgerBot[]
  onDetail: (id: string) => void
  bos: boolean
} & SecimProps) {
  if (!rows.length) {
    return <div className="shadow-empty">{bos ? 'Kayıtlı bot yok.' : ''}</div>
  }
  const DURUM: Record<string, { ad: string }> = {
    RUNNING: { ad: 'Çalışıyor' },
    PAUSED: { ad: 'Duraklatıldı' },
    REMOVED: { ad: 'Kaldırıldı' },
  }
  // Her durumdaki bot silinebilir — çalışan/duraklıyı sunucu önce kaldırır
  // (emirleri iptal). Eski kural (yalnız REMOVED) kullanıcıyı kilitliyordu:
  // kontratı listeden düşmüş botun "kaldır" düğmesine ulaşılamıyordu.
  const uygunlar = rows.map((b) => b.id)
  return (
    <table className="ledger-table">
      <thead>
        <tr>
          <th className="sec-col">
            <TumunuSec uygunlar={uygunlar} secim={secim} setSecim={setSecim} />
          </th>
          <th />
          <th className="lg-kimlik">Bot</th>
          <th>Tip</th>
          <th>Kontrat</th>
          <th>Durum</th>
          <th>Kuruluş</th>
          <th className="num">Emir</th>
          <th className="num" title="Toplam alış — ağırlıklı ortalama fiyatıyla">Alış</th>
          <th className="num" title="Toplam satış — ağırlıklı ortalama fiyatıyla">Satış</th>
          <th className="num">Net</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => {
          // Kontratı kapanan bot "Çalışıyor" gösterilmez (kullanıcı yakaladı
          // 2026-07-29): DB durumu RUNNING kalsa da piyasa bitti. Kaldırılmış
          // bot yine "Kaldırıldı" der — o karar kullanıcının, kapanışın değil.
          const d = b.expired && b.status !== 'REMOVED'
            ? { ad: 'Kontrat kapandı', ton: 'EXPIRED' }
            : { ...(DURUM[b.status] ?? { ad: b.status }), ton: b.status }
          return (
            <tr key={b.id} className={secim.has(b.id) ? 'secili' : undefined}>
              <td className="sec-col">
                <input
                  type="checkbox"
                  checked={secim.has(b.id)}
                  title={b.status === 'REMOVED'
                    ? 'Bot TÜM iziyle silinir: kararları, emirleri, eşleşmeleri'
                    : 'Silinirken önce durdurulur, açık emirleri iptal edilir; ' +
                      'sonra TÜM iziyle silinir'}
                  onChange={(e) => sec(b.id, e.target.checked)}
                  aria-label={`${b.id} seç`}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="icon-btn info"
                  onClick={() => onDetail(b.id)}
                  title={`${b.id} raporunu aç — kararlar, emirler, eşleşmeler`}
                  aria-label={`${b.id} bot raporu`}
                >
                  ⓘ
                </button>
              </td>
              <td className="lg-kimlik">
                <code>{b.id}</code>
              </td>
              <td className="muted">{b.type}</td>
              <td>{b.contract}</td>
              <td>
                <span className={`brapor-durum ${d.ton}`}>{d.ad}</span>
              </td>
              <td className="muted">{clock(b.createdAt)}</td>
              <td className="num">{b.orderCount}</td>
              <td className="num">
                {b.bought > 0 ? `${qty(b.bought)} @ ${fmtPrice(b.buyAvg)}` : '–'}
              </td>
              <td className="num">
                {b.sold > 0 ? `${qty(b.sold)} @ ${fmtPrice(b.sellAvg)}` : '–'}
              </td>
              <td className={`num${b.net > 0 ? ' yon-al' : b.net < 0 ? ' yon-sat' : ' muted'}`}>
                {b.net === 0 ? '–' : qty(b.net)}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}


/** Seçim aracının ortak parçaları — üç tabloda aynı davranış. */
interface SecimProps {
  secim: Set<string | number>
  sec: (id: string | number, secili: boolean) => void
  setSecim: (s: Set<string | number>) => void
}

/** Başlık kutusu: sayfadaki SEÇİLEBİLİR satırların tümünü seçer/bırakır. */
function TumunuSec({
  uygunlar,
  secim,
  setSecim,
}: {
  uygunlar: (string | number)[]
} & Pick<SecimProps, 'secim' | 'setSecim'>) {
  const hepsi = uygunlar.length > 0 && uygunlar.every((id) => secim.has(id))
  return (
    <input
      type="checkbox"
      checked={hepsi}
      disabled={uygunlar.length === 0}
      onChange={(e) => setSecim(new Set(e.target.checked ? uygunlar : []))}
      title="Sayfadaki seçilebilir kayıtların tümü"
      aria-label="Tümünü seç"
    />
  )
}

function OrdersTable({
  rows,
  onDetail,
  bos,
  secim,
  sec,
  setSecim,
}: {
  rows: LedgerOrder[]
  onDetail: (id: string) => void
  bos: boolean
} & SecimProps) {
  if (!rows.length) {
    return <div className="shadow-empty">{bos ? 'Bu filtrelerle kayıt yok.' : ''}</div>
  }
  // Açık emir silinemez (sunucu da reddeder) — kutusu da kapalı.
  const uygunlar = rows.filter((o) => o.status !== 'OPEN').map((o) => o.id)
  return (
    <table className="ledger-table">
      <thead>
        <tr>
          <th className="sec-col">
            <TumunuSec uygunlar={uygunlar} secim={secim} setSecim={setSecim} />
          </th>
          <th />
          <th className="lg-kimlik">Emir</th>
          <th>Kontrat</th>
          <th>Yön</th>
          <th className="num">Fiyat</th>
          <th className="num">Hacim</th>
          <th title="Emrin hangi yöntemlerle dolduğu — karışık olabilir">Dolum</th>
          <th>Açılış</th>
          <th>Son değişiklik</th>
          <th className="num">Sürüm</th>
          <th>Durum</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => {
          const d = durumBilgisi(o.status)
          return (
            <tr key={o.id} className={secim.has(o.id) ? 'secili' : undefined}>
              <td className="sec-col">
                <input
                  type="checkbox"
                  checked={secim.has(o.id)}
                  disabled={o.status === 'OPEN'}
                  title={o.status === 'OPEN' ? 'Açık emir silinemez — önce iptal et' : undefined}
                  onChange={(e) => sec(o.id, e.target.checked)}
                  aria-label={`${o.id} seç`}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="icon-btn info"
                  onClick={() => onDetail(o.id)}
                  title={`${o.id} geçmişini gör`}
                  aria-label={`${o.id} emrinin geçmişi`}
                >
                  ⓘ
                </button>
              </td>
              <td className="lg-kimlik">
                <code>{o.id}</code>
              </td>
              <td>{o.contract}</td>
              <td className={o.side === 'BUY' ? 'yon-al' : 'yon-sat'}>
                {o.side === 'BUY' ? '▲ Alış' : '▼ Satış'}
              </td>
              <td className="num">{fmtPrice(o.price)}</td>
              <td className="num" title={`${qty(o.remaining)} lot kaldı`}>
                {qty(o.filledQuantity)}/{qty(o.quantity)}
              </td>
              {/* Kırılım: "6 Süpürdük · 4 Simüle" — hiç dolmadıysa çizgi. */}
              <td className="muted">{dolumOzeti(o.fillBreakdown) || '–'}</td>
              <td className="muted">{clock(o.createdAt)}</td>
              <td className="muted">{clock(o.updatedAt)}</td>
              <td className="num muted">v{o.version}</td>
              <td>
                <span className={`durum-rozet ${d.tone}`} title={d.label}>
                  {d.code}
                </span>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function FillsTable({
  rows,
  onDetail,
  bos,
  secim,
  sec,
  setSecim,
}: {
  rows: LedgerFill[]
  onDetail: (id: string) => void
  bos: boolean
} & SecimProps) {
  if (!rows.length) {
    return <div className="shadow-empty">{bos ? 'Bu filtrelerle kayıt yok.' : ''}</div>
  }
  return (
    <table className="ledger-table">
      <thead>
        <tr>
          <th className="sec-col">
            <TumunuSec uygunlar={rows.map((f) => f.id)} secim={secim} setSecim={setSecim} />
          </th>
          <th />
          <th className="lg-kimlik">Zaman</th>
          <th>Kontrat</th>
          <th>Yön</th>
          <th title="Dolumun kaynağı: Simüle / Süpürdük / Süpürüldük">Tip</th>
          <th className="num">Miktar</th>
          <th className="num">Bizim fiyat</th>
          <th className="num">İşlem fiyatı</th>
          <th className="num" title="Aradaki fark bize gelmez — agresör öderdi.">
            Fark
          </th>
          {/* Dolum anındaki tahta: başka hiçbir yerde saklanmıyor. */}
          <th className="num">Tahta (alış/satış)</th>
          <th className="num">PTF</th>
          <th>Emir</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f) => (
          <tr key={f.id} className={secim.has(f.id) ? 'secili' : undefined}>
            <td className="sec-col">
              <input
                type="checkbox"
                checked={secim.has(f.id)}
                onChange={(e) => sec(f.id, e.target.checked)}
                aria-label={`${f.orderId} eşleşmesini seç`}
              />
            </td>
            <td>
              <button
                type="button"
                className="icon-btn info"
                onClick={() => onDetail(f.orderId)}
                title={`${f.orderId} geçmişini gör`}
                aria-label={`${f.orderId} emrinin geçmişi`}
              >
                ⓘ
              </button>
            </td>
            <td className="muted lg-kimlik">{clock(f.ts)}</td>
            <td>{f.contract}</td>
            <td className={f.side === 'BUY' ? 'yon-al' : 'yon-sat'}>
              {f.side === 'BUY' ? '▲ Alış' : '▼ Satış'}
            </td>
            <td>
              <span className={`fill-tag ${f.liquidity ?? 'trade'}`} title={dolumAciklama(f.liquidity)}>
                {dolumEtiketi(f.liquidity)}
              </span>
            </td>
            <td className="num" title={`${mwh(f.quantity)} MWh`}>
              {qty(f.quantity)}
            </td>
            <td className="num">{fmtPrice(f.price)}</td>
            <td className="num muted">{fmtPrice(f.tradePrice)}</td>
            <td className="num">{fmtPrice(f.priceAdvantage)}</td>
            <td className="num muted">
              {f.bestBid == null && f.bestAsk == null
                ? '–'
                : `${fmtPrice(f.bestBid)} / ${fmtPrice(f.bestAsk)}`}
            </td>
            <td className="num muted">{fmtPrice(f.mcp)}</td>
            <td>
              <code className="muted">{f.orderId}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
